import { Capacitor } from "@capacitor/core";
import { usePostHog } from "posthog-js/react";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useIntercom } from "react-use-intercom";
import type { ElementOf } from "ts-essentials";
import { authFlowController } from "~/config/auth0/auth-flow-controller";
import type { UserPropsAuth0 } from "~/config/auth0/use-auth0";
import { getAuth0Id, useAuth0 } from "~/config/auth0/use-auth0";
import { useNetwork } from "~/config/capacitor/network/hook";
import { env } from "~/config/env";
import { type Language, i18n } from "~/config/i18next";
import { captureException, SentryReact } from "~/config/sentry";
import { setStreamChatUser } from "~/config/stream-chat/helpers";
import { SpinLoader } from "~/design-system/Spinner/SpinLoader";
import { Stack } from "~/design-system/Stack";
import { Text } from "~/design-system/Text/Text";
import { FullPageSpinner } from "~/screens/App/components/FullPageSpinner/FullPageSpinner";
import { UnavailableStateView } from "~/screens/App/screens/UnavailableScreen/UnavailableStateView";
import { usePostHogFeatureFlags } from "~/screens/App/utils/usePostHogFeatureFlags/usePostHogFeatureFlags";
import { createComponentHook } from "~/types";
import { useSegment } from "../segment/SegmentProvider";
import { UserNotificationsProvider } from "./user-notifications/UserNotifications";
import type {
  UserProvider_UserQuery,
  UserTeams_DataFragment,
} from "./UserProvider.graphql";
import { useUserProvider_UserQuery } from "./UserProvider.graphql";
import { WorkerStatusProvider } from "./workerstatus/WorkerStatus";

type UserContextType = {
  user: ElementOf<UserProvider_UserQuery["users"]>;
  getUserTeamsByProjectId: (projectId: string) => UserTeams_DataFragment[];
};

const UserContext = React.createContext<UserContextType>({} as UserContextType);

/**
 * Hook to get the user context
 *
 * **Warning**: There are provided a function *getUserTeamsByProjectId*
 * to easily get the user teams by project id
 * because the user.teams has all user projects
 */
function useUser() {
  return React.useContext(UserContext);
}

type UserConfigProps = {
  children: React.ReactNode;
};

const useUserProvider = createComponentHook(() => {
  const { isLoading: isAuth0Loading, user } = useAuth0<UserPropsAuth0>();
  const navigate = useNavigate();
  const hasRequestedReauthRef = React.useRef(false);

  React.useEffect(() => {
    if (user) {
      hasRequestedReauthRef.current = false;
    }
  }, [user]);

  const requestReauthOnce = React.useCallback(
    (
      source:
        | "user_provider_missing_auth0_user"
        | "user_provider_user_query_error"
    ) => {
      if (hasRequestedReauthRef.current) {
        return;
      }

      hasRequestedReauthRef.current = true;
      authFlowController.requestReauth(source);
    },
    []
  );

  const requestReauthForMissingUser = React.useCallback(() => {
    if (hasRequestedReauthRef.current) {
      return;
    }

    hasRequestedReauthRef.current = true;
    authFlowController.requestReauth("user_provider_missing_auth0_user");
  }, []);

  console.info("🔌 Clovis useUserProvider useUserProvider_UserQuery", {
    auth0_id: getAuth0Id(user),
    user,
  });

  if (!user) {
    if (!isAuth0Loading) {
      console.warn(
        "🔌 Clovis useUserProvider user is null: triggering single reauth request for recovery"
      );
      requestReauthForMissingUser();
    }
    return;
  }

  const [res] = useUserProvider_UserQuery({
    variables: {
      auth0_id: getAuth0Id(user) ?? user?.sub,
    },
  });

  const { data, error, fetching } = res;

  const SegmentAnalyticsContext = useSegment();
  const IntercomContext = useIntercom();
  const posthog = usePostHog();

  usePostHogFeatureFlags();

  const currentUser = data?.users[0];
  const missingDatabaseUser =
    fetching === false &&
    !error &&
    user != null &&
    Array.isArray(data?.users) &&
    currentUser == null;
  const auth0Email =
    user["https://hasura.io/jwt/claims"]?.["x-hasura-user-email"];

  const getUserTeamsByProjectId = React.useCallback(
    (projectId: string) => {
      if (!currentUser) {
        return [];
      }
      return currentUser.teams.filter((team) => {
        return team.team.project_id === projectId;
      });
    },
    [currentUser]
  );

  const isError =
    !missingDatabaseUser &&
    fetching === false &&
    !currentUser &&
    res.operation &&
    res.operation.context.meta?.cacheOutcome !== "miss";

  if (isError) {
    /* Having an jwt error here may come from:
      - if you changed of branch, verify that your ran well yarn clean %% yarn install at the root of the repo to reinstall all the right dependencies
      - verify that no other node process are still running on your computer (restart your computer)
      - a wrong seed on the backend when running yarn dev (check opening hasura console on http://localhost:9695/ or http://localhost:8795/)
      - a wrong HASURA_GRAPHQL_JWT_SECRET variable in server (check that you don't use the test HASURA JWT)
      - not matching userId and auth0_id between the dev database and auth0 dev account (check inside auth0 and hasura console to verify the ids are the same)
      - a wrong clock in your terminal or computer (reboot it)
    */
    captureException([
      "Error while fetching database user with Auth0 id and token, check inline doc in code to solve it under dev :",
      error,
    ]);
    requestReauthOnce("user_provider_user_query_error");
  }

  React.useEffect(() => {
    console.info("currentUser?.language", currentUser?.language);

    if (currentUser?.language) void i18n.changeLanguage(currentUser?.language);
  }, [currentUser?.language]);

  /* Initialise StreamChat with current user stream_user_id */
  React.useEffect(() => {
    async function initChat() {
      if (currentUser?.stream_chat_token?.token.length) {
        await setStreamChatUser({
          id: currentUser.stream_user_id,
          language: i18n.language as Language,
          token: currentUser.stream_chat_token.token,
        });
      }
    }
    void initChat();
  }, [currentUser]);

  React.useEffect(() => {
    if (SegmentAnalyticsContext && currentUser) {
      console.info("Initialize Segment Analytics User");
      void SegmentAnalyticsContext.identify(currentUser.id);
      if (currentUser.active_org) {
        void SegmentAnalyticsContext.group(currentUser.active_org.org.id);
      }
    }
    console.info("Initialize PostHog Analytics User");
    if (posthog && currentUser) {
      const posthogUserProperties = {
        email: currentUser.contact?.email ?? "",
      };

      // Reset PostHog to clear any anonymous ID
      posthog.reset();
      posthog.setPersonPropertiesForFlags(posthogUserProperties, false);
      // Identify with the actual user ID
      posthog.identify(currentUser.id, posthogUserProperties);
      posthog.reloadFeatureFlags();
    }

    if (IntercomContext && currentUser) {
      console.info("Initialize Intercom Analytics User");
      /* Just give to Intercom the email and userId, as the other values will be catched by Segment */
      void IntercomContext?.boot({
        ...(currentUser.active_org && {
          company: {
            companyId: currentUser.active_org.org.id,
            name: currentUser.active_org.org.name,
          },
        }),
        customAttributes: {
          clovis_app_version: env.VITE_RELEASE,
        },
        email: currentUser.contact?.email ?? "",
        languageOverride: currentUser.language,
        userId: currentUser.id,
      });
    }
  }, [currentUser, IntercomContext, SegmentAnalyticsContext, posthog]);

  React.useEffect(() => {
    // If a user is "locked" => redirect to unavailable page
    if (currentUser?.is_locked) {
      navigate("/unavailable", { replace: true });
    }
  }, [currentUser, navigate]);

  return {
    actions: {
      getUserTeamsByProjectId,
    },
    state: {
      auth0Email,
      missingDatabaseUser,
      user: currentUser,
    },
  };
});

// Safety timeout: if user data never loads, request reauth to prevent infinite spinner
// 15s is generous enough to handle slow backends/networks without false positives
const USER_FETCH_TIMEOUT_MS = 15000;
// Show the countdown message after this delay (gives the app time to load normally)
const USER_FETCH_SHOW_MESSAGE_AFTER_MS = 5000;

/**
 * Full page spinner with a countdown message indicating the user will be
 * redirected to the login screen. Shown when user data takes too long to load.
 */
function FullPageSpinnerWithCountdown({
  secondsLeft,
}: {
  secondsLeft: number;
}): JSX.Element {
  const { t } = useTranslation();

  return (
    <div
      style={{
        alignItems: "center",
        display: "flex",
        height: "100vh",
        width: "100vw",
      }}
    >
      <Stack space={"large"} align={"center"} width={"full"}>
        <SpinLoader />
        <Text size="standard" tone="secondary" align="center">
          {t(
            "UserProvider.sessionExpiredRedirecting",
            "Session expired. Redirecting to login in {{count}}s…",
            { count: secondsLeft }
          )}
        </Text>
      </Stack>
    </div>
  );
}

function UserProvider(props: UserConfigProps) {
  const hookResult = useUserProvider();
  const { isLoading: isAuthLoading } = useAuth0<UserPropsAuth0>();
  const { isOffline } = useNetwork();
  const hasRequestedTimeoutReauthRef = React.useRef(false);
  const hasCapturedMissingDatabaseUserRef = React.useRef(false);

  const hasUser = hookResult?.state?.user != null;
  const isMissingDatabaseUser = hookResult?.state?.missingDatabaseUser === true;

  React.useEffect(() => {
    if (!isMissingDatabaseUser) {
      hasCapturedMissingDatabaseUserRef.current = false;
      return;
    }

    if (hasCapturedMissingDatabaseUserRef.current) {
      return;
    }

    hasCapturedMissingDatabaseUserRef.current = true;
    captureException(
      new Error("Authenticated user missing from database provisioning"),
      {
        extra: {
          auth0Email: hookResult?.state?.auth0Email,
        },
      }
    );
  }, [hookResult?.state?.auth0Email, isMissingDatabaseUser]);

  React.useEffect(() => {
    if (hasUser) {
      hasRequestedTimeoutReauthRef.current = false;
    }
  }, [hasUser]);

  // Track how many seconds remain before forced reauth request
  const [secondsLeft, setSecondsLeft] = React.useState<number | null>(null);

  // Session-expired countdown + reauth timeout: mobile only (prevents showing
  // "Session expired" on web when e.g. user exists in Auth0 but not in DB).
  const isNative = Capacitor.isNativePlatform();

  React.useEffect(() => {
    // Do not force reauth while auth bootstrap is running or while offline.
    if (hasUser || isAuthLoading || isOffline) {
      setSecondsLeft(null);
      return;
    }

    // On web, never show the "Session expired" countdown or trigger reauth by timeout.
    if (!isNative) {
      setSecondsLeft(null);
      return;
    }

    // Each new effect cycle (dependencies changed) gets a fresh attempt.
    hasRequestedTimeoutReauthRef.current = false;

    // After USER_FETCH_SHOW_MESSAGE_AFTER_MS, start showing the countdown
    const showMessageTimer = setTimeout(() => {
      const remaining = Math.ceil(
        (USER_FETCH_TIMEOUT_MS - USER_FETCH_SHOW_MESSAGE_AFTER_MS) / 1000
      );
      setSecondsLeft(remaining);
    }, USER_FETCH_SHOW_MESSAGE_AFTER_MS);

    const timer = setTimeout(() => {
      if (hasRequestedTimeoutReauthRef.current) {
        return;
      }
      hasRequestedTimeoutReauthRef.current = true;

      captureException(
        "UserProvider timed out waiting for user data - requesting reauth to recover from stuck spinner"
      );
      authFlowController.requestReauth("user_provider_fetch_timeout");
    }, USER_FETCH_TIMEOUT_MS);

    return () => {
      clearTimeout(showMessageTimer);
      clearTimeout(timer);
    };
  }, [hasUser, isAuthLoading, isOffline, isNative]);

  // Tick down every second once the countdown message is visible
  React.useEffect(() => {
    if (secondsLeft == null || secondsLeft <= 0) return;

    const interval = setInterval(() => {
      setSecondsLeft((prev) => (prev != null && prev > 0 ? prev - 1 : 0));
    }, 1000);

    return () => clearInterval(interval);
  }, [secondsLeft != null]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle the case where useUserProvider returns void/undefined
  if (!hookResult) {
    if (secondsLeft != null) {
      return <FullPageSpinnerWithCountdown secondsLeft={secondsLeft} />;
    }
    return <FullPageSpinner />;
  }

  const { actions, state } = hookResult;

  if (state.missingDatabaseUser) {
    return (
      <UnavailableStateView email={state.auth0Email} variant="notProvisioned" />
    );
  }

  /* Show a void page while fetching the user
     Warning: this may render a void page under dev if auth0 is not fully synced with the database */
  if (!state.user) {
    if (secondsLeft != null) {
      return <FullPageSpinnerWithCountdown secondsLeft={secondsLeft} />;
    }
    return <FullPageSpinner />;
  }

  // Setup the logged user for all our sentry context
  SentryReact.setUser(state.user);

  const value = { ...state, ...actions };

  return (
    // cast our state as we checked that user isn't null
    <UserContext.Provider value={value as UserContextType}>
      <UserNotificationsProvider userId={state.user.id}>
        <WorkerStatusProvider userId={state.user.id}>
          {props.children}
        </WorkerStatusProvider>
      </UserNotificationsProvider>
    </UserContext.Provider>
  );
}

export type { UserContextType };
export { UserProvider, useUser };
